import { useState, useCallback } from 'react';
import { FormValidationErrors } from '../types';

interface ValidationRule {
  required?: boolean;
  min?: number;
  max?: number;
  pattern?: RegExp;
  custom?: (value: any) => string | undefined;
}

interface ValidationRules {
  [key: string]: ValidationRule;
}

interface UseFormValidationResult<T> {
  errors: FormValidationErrors;
  validate: (data: T) => boolean;
  validateField: (field: keyof T, value: any) => string | undefined;
  clearError: (field: keyof T) => void;
  clearAllErrors: () => void;
}

export const useFormValidation = <T extends Record<string, any>>(
  rules: ValidationRules
): UseFormValidationResult<T> => {
  const [errors, setErrors] = useState<FormValidationErrors>({});

  const validateField = useCallback((field: keyof T, value: any): string | undefined => {
    const rule = rules[field as string];
    if (!rule) return undefined;

    const stringValue = String(value || '').trim();

    if (rule.required && !stringValue) {
      return `${String(field)} is required`;
    }

    if (rule.min && stringValue.length < rule.min) {
      return `${String(field)} must be at least ${rule.min} characters`;
    }

    if (rule.max && stringValue.length > rule.max) {
      return `${String(field)} must be no more than ${rule.max} characters`;
    }

    if (rule.pattern && stringValue && !rule.pattern.test(stringValue)) {
      return `${String(field)} format is invalid`;
    }

    if (rule.custom) {
      return rule.custom(value);
    }

    return undefined;
  }, [rules]);

  const validate = useCallback((data: T): boolean => {
    const newErrors: FormValidationErrors = {};

    Object.keys(rules).forEach(field => {
      const error = validateField(field as keyof T, data[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [rules, validateField]);

  const clearError = useCallback((field: keyof T) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field as string];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    errors,
    validate,
    validateField,
    clearError,
    clearAllErrors
  };
};
